import React, { useState } from 'react';
import type { InputRef } from 'antd';
import AppTextField from './AppTextField';

type AppPhoneNumberProps = Omit<
  React.ComponentProps<typeof AppTextField>,
  'onChange' | 'maxLength'
> & {
  onChange?: (value: string) => void;
};

const toHalfWidth = (str: string) =>
  str
    .replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[－ー―‐]/g, '-');

const MAX_LENGTH = 13;

const sanitize = (raw: string): string => {
  const half = toHalfWidth(raw);
  return half.replace(/[^\d-]/g, '').slice(0, MAX_LENGTH);
};

const AppPhoneNumber = React.forwardRef<InputRef, AppPhoneNumberProps>(
  ({ onChange, value = '', placeholder = '入力してください', ...props }, ref) => {
    const [isComposing, setIsComposing] = useState(false);
    const stringValue = String(value ?? '');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isComposing) {
        onChange?.(e.target.value);
        return;
      }
      onChange?.(sanitize(e.target.value));
    };

    const handleCompositionStart = () => {
      setIsComposing(true);
    };

    const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
      setIsComposing(false);
      onChange?.(sanitize(e.currentTarget.value));
    };

    return (
      <AppTextField
        ref={ref}
        {...props}
        value={stringValue}
        onChange={handleChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        placeholder={placeholder}
        maxLength={MAX_LENGTH}
      />
    );
  }
);

AppPhoneNumber.displayName = 'AppPhoneNumber';

export default AppPhoneNumber;
